import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Badge } from '@/components/shadcn/badge'
import { Switch } from '@/components/shadcn/switch'
import { IconCloseOutline } from '@icons/IconCloseOutline'

import { useCurrentApplication } from '../useCurrentApplication'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

export type CollaborationChannel = RestSchemas['Messages.Collaboration.CollaborationChannel']
type NewCollaborationChannel = RestSchemas['Messages.Collaboration.NewCollaborationChannel']

const formSchema = z.object({
  key: z.string().min(1, 'Key is required').max(100, 'Key cannot exceed 100 characters'),
  value: z.string().min(1, 'Value is required').max(300, 'Value cannot exceed 300 characters'),
  hashtags: z.array(z.string().min(1)).optional(),
  emailsEnabled: z.boolean().optional().default(false),
})

export type TAddCollaborationChannelForm = z.infer<typeof formSchema>

type AddCollaborationChannelFormV2Props = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedChannel?: CollaborationChannel
}

export const AddCollaborationChannelFormV2 = (props: AddCollaborationChannelFormV2Props) => {
  console.log('props.selectedChannel', props.selectedChannel)
  const { applicationId } = useCurrentApplication()
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedChannel

  const {
    putCollaborationApiCreateCollaborationChannel,
    putCollaborationApiUpdateCollaborationChannel,
    getCollaborationApiGetCollaborationChannels,
  } = useRestClient()

  const { mutate: updateChannel, isPending: isUpdating } = putCollaborationApiUpdateCollaborationChannel.useMutation(
    undefined,
    {
      onSuccess: () => {
        getCollaborationApiGetCollaborationChannels.invalidateQueries({ parameters: { path: { applicationId } } })
        if (props.sheetTrigger) {
          setSheetOpen(false)
        }
        toast.success('Collaboration channel successfully updated')
        props.onSubmit?.()
      },
      onError: () => {
        toast.error('Error updating collaboration channel')
      },
    }
  )

  const { mutate: createChannel, isPending: isAdding } = putCollaborationApiCreateCollaborationChannel.useMutation(
    undefined,
    {
      onSuccess: () => {
        getCollaborationApiGetCollaborationChannels.invalidateQueries({ parameters: { path: { applicationId } } })
        if (props.sheetTrigger) {
          setSheetOpen(false)
        }
        toast.success('Collaboration channel successfully added')
        props.onSubmit?.()
      },
      onError: () => {
        toast.error('Error adding collaboration channel')
      },
    }
  )

  const submitHandler = useCallback(
    (data: TAddCollaborationChannelForm) => {
      const sanitizedHashtags = (data.hashtags || [])
        .map((h) => h.trim())
        .filter((h) => h.length > 0)
        .map((h) => (h.startsWith('#') ? h : `#${h}`))
      const hashtagsJson = JSON.stringify(sanitizedHashtags)

      if (edit && props.selectedChannel?.id) {
        const updated: CollaborationChannel = {
          id: props.selectedChannel.id,
          applicationId,
          key: data.key,
          value: data.value,
          hashtags: hashtagsJson,
          emailsEnabled: data.emailsEnabled ?? false,
        }

        updateChannel({
          body: updated,
          path: { applicationId, channelId: props.selectedChannel.id },
        })
      } else {
        const created: NewCollaborationChannel = {
          applicationId,
          key: data.key,
          value: data.value,
          hashtags: hashtagsJson,
          emailsEnabled: data.emailsEnabled ?? false,
        }
        createChannel({ body: created, path: { applicationId } })
      }
    },
    [edit, props.selectedChannel, applicationId, updateChannel, createChannel]
  )

  const isLoading = isUpdating || isAdding
  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle || (edit ? 'Edit Collaboration Channel' : 'Create Collaboration Channel')}
      description={
        props.sheetDescription ||
        (edit ? 'Update the collaboration channel details' : 'Add a new collaboration channel')
      }
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (
  props: AddCollaborationChannelFormV2Props & {
    submitHandler: (data: TAddCollaborationChannelForm) => void
  }
) => {
  const selected: Partial<CollaborationChannel> = props.selectedChannel ?? {}
  const { key, value, hashtags, emailsEnabled } = selected
  const edit = !!props.selectedChannel

  const parsedHashtags: string[] = useMemo(() => {
    if (!hashtags) return []
    try {
      const parsed = JSON.parse(hashtags)
      return Array.isArray(parsed) ? parsed.map((h: string) => h ?? '') : []
    } catch (_err) {
      return []
    }
  }, [hashtags])

  const defaultValues = useMemo(() => {
    return {
      key: key ?? '',
      value: value ?? '',
      hashtags: parsedHashtags,
      emailsEnabled: emailsEnabled ?? false,
    }
  }, [key, value, parsedHashtags, emailsEnabled])

  const form = useForm<TAddCollaborationChannelForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })
  const [newHashtag, setNewHashtag] = useState<string>('')

  const handleAddHashtag = () => {
    const raw = newHashtag.trim()
    if (!raw) return
    const formatted = raw.startsWith('#') ? raw : `#${raw}`
    const next = Array.from(new Set([...(form.getValues('hashtags') || []), formatted]))
    form.setValue('hashtags', next, { shouldDirty: true, shouldTouch: true })
    setNewHashtag('')
  }

  const handleRemoveHashtag = (value: string) => {
    const current = form.getValues('hashtags') || []
    const next = current.filter((v) => v !== value)
    form.setValue('hashtags', next, { shouldDirty: true, shouldTouch: true })
  }

  const handleHashtagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddHashtag()
    }
  }

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              {/* Key Field */}
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., risks, feedback, discussions" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Value Field */}
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Value *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., Comment on the resources" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Hashtags */}
              <div className="space-y-2">
                <FormItem>
                  <FormLabel>Hashtags</FormLabel>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newHashtag}
                      onChange={(e) => setNewHashtag(e.target.value)}
                      onKeyDown={handleHashtagKeyDown}
                      placeholder="Type a hashtag and press Enter"
                    />
                  </div>
                  {(form.watch('hashtags') || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(form.watch('hashtags') || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                          {tag}
                          <button
                            type="button"
                            className="ring-offset-offwhite ml-1 rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            onClick={() => handleRemoveHashtag(tag)}
                          >
                            <IconCloseOutline className="size-3" />
                            <span className="sr-only">Remove</span>
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </FormItem>
              </div>

              {/* Emails Enabled */}
              <FormField
                control={form.control}
                name="emailsEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Emails Enabled</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        Enable email notifications for this collaboration channel
                      </div>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="form-action-container">
              <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}

export default AddCollaborationChannelFormV2
